import { useMemo, useState, useEffect } from 'react';
import { Box, Typography, Grid, Collapse, Pagination, TextField, InputAdornment, useTheme } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';

import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';

// Validated categorical palette (contrast + color blindness) in both themes.
// The order is fixed: each category gets a stable slot, never the rank's color.
export const CATEGORY_COLORS = {
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'],
  dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
};
const OTHER_COLOR = { light: '#8a8a82', dark: '#a6a69c' };

const HEX = /^#([0-9a-f]{6})$/i;

// Pulls a color towards white/black keeping its hue
const mixToward = (hex, target, amount) => {
  const match = HEX.exec(hex);
  if (!match) return hex;
  const from = parseInt(match[1], 16);
  const to = parseInt(target, 16);
  const channel = (shift) => {
    const a = (from >> shift) & 0xff;
    const b = (to >> shift) & 0xff;
    return Math.round(a + (b - a) * amount);
  };
  const mixed = (channel(16) << 16) | (channel(8) << 8) | channel(0);
  return `#${mixed.toString(16).padStart(6, '0')}`;
};

// The palette has 8 validated slots. A block that lists every category instead of
// folding the tail into "Outros" may need more than that, so the extra rounds reuse
// the same hues at another lightness — still the validated hue set, still telling
// two neighbouring slices apart.
const paletteFor = (mode, count) => {
  const base = CATEGORY_COLORS[mode];
  if (count <= base.length) return base;
  const target = mode === 'dark' ? 'ffffff' : '000000';
  const colors = [...base];
  for (let round = 1; round < Math.ceil(count / base.length); round++) {
    const amount = Math.min(0.6, 0.3 * round);
    colors.push(...base.map((color) => mixToward(color, target, amount)));
  }
  return colors;
};

// One slice per palette color, so no two slices of a donut ever share one. The
// smaller categories fold into "Outros", which is not a bucket of its own: it
// opens into those same categories, each with its full breakdown underneath.
// A block may opt out of the fold (maxSlices) and list every category instead.
const MAX_SLICES = 8;
const CHILD_PAGE_SIZE = 10;    // cost centers / clients per page
const ENTRY_PAGE_SIZE = 10;    // entries per page on the third level

// Only once the ring goes past the palette — "Outros" split open, or a block that
// never folds: dozens of slices means dozens of leader lines, so below this share
// the outer % is dropped. The list still shows it.
const LABEL_MIN_PERCENT = 2;

export const formatNumber = (value) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);

export const formatPercent = (value) =>
  `${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0)}%`;

export const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
};

// Builds the slices from categories { id, name, value, children, colorKey, icon }.
// colorKey picks the color slot (stable identity); the display order is by value.
// A category may also pin its own `color` when the reading is fixed (segments).
const buildSlices = (categories, mode, keepZeroValues = false, maxSlices = MAX_SLICES, foldBelow = 0) => {
  // A zero has no slice to draw, so by default it is dropped. Reports where the
  // row itself is the fact — a cancelled client with no maintenance contract —
  // keep it: it still belongs on the list.
  const positives = categories.filter((c) => (keepZeroValues ? c.value >= 0 : c.value > 0));

  const sorted = [...positives].sort((a, b) => b.value - a.value);
  const cut = foldBelow > 0
    ? sorted.findIndex((c) => c.value < foldBelow)
    : maxSlices;
  const limit = cut < 0 ? sorted.length : cut;
  const visible = sorted.slice(0, limit);
  const rest = sorted.slice(limit);

  // Whatever a pinned category took is out of the rotation, so no two
  // categories in the same block end up with the same color
  const pinned = new Set(positives.map((c) => c.color).filter(Boolean));
  const palette = paletteFor(mode, visible.length + pinned.size);
  const rotation = palette.filter((color) => !pinned.has(color));
  const free = rotation.length ? rotation : palette;

  // Only the visible slices compete for palette slots, so a donut never repeats
  // a color. Ordering by colorKey instead of by rank keeps a category on the
  // same color from one period to the next.
  const colorSlot = new Map(
    [...visible]
      .filter((c) => !c.color)
      .sort((a, b) => String(a.colorKey).localeCompare(String(b.colorKey), 'pt-BR', { numeric: true }))
      .map((c, i) => [c.id, free[i % free.length]])
  );

  const slices = visible.map((c) => ({ ...c, color: c.color || colorSlot.get(c.id) }));

  if (rest.length) {
    slices.push({
      id: '__other__',
      name: 'Outros',
      icon: <FolderIcon />,
      color: OTHER_COLOR[mode],
      value: +rest.reduce((acc, c) => acc + c.value, 0).toFixed(2),
      // Not children: these are categories, and they open like any other one.
      // They have no slice of their own, so the color here is just a list marker.
      nested: rest.map((c, i) => ({ ...c, color: c.color || free[i % free.length] })),
    });
  }

  const total = +slices.reduce((acc, c) => acc + c.value, 0).toFixed(2);
  return { slices, total };
};

// Places the outer labels (leader line + %) avoiding collisions: stacks them
// vertically per side of the donut respecting a minimum spacing.
const buildLabelLayout = (slices, total, { cx, cy, outerRadius }, minPercent = 0) => {
  const GAP = 16;
  const ELBOW = 16;
  const items = [];
  let accumulated = 0;

  for (const slice of slices) {
    const startFraction = total ? accumulated / total : 0;
    const fraction = total ? slice.value / total : 0;
    accumulated += slice.value;

    // The donut starts at 12 o'clock and runs clockwise (startAngle 90 -> endAngle -270)
    const rad = ((90 - (startFraction + fraction / 2) * 360) * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    // Angles accumulate over every slice, but a skipped label must not take part
    // in the collision pass — otherwise it would push the visible ones apart
    if (fraction * 100 < minPercent) continue;

    items.push({
      id: slice.id,
      color: slice.color,
      percent: fraction * 100,
      side: cos >= 0 ? 1 : -1,
      sx: cx + cos * outerRadius,
      sy: cy - sin * outerRadius,
      ex: cx + cos * (outerRadius + ELBOW),
      ey: cy - sin * (outerRadius + ELBOW),
    });
  }

  for (const side of [1, -1]) {
    const sideItems = items.filter((i) => i.side === side).sort((a, b) => a.ey - b.ey);
    // Push down whatever collides
    for (let i = 1; i < sideItems.length; i++) {
      if (sideItems[i].ey - sideItems[i - 1].ey < GAP) sideItems[i].ey = sideItems[i - 1].ey + GAP;
    }
    // If it overflowed at the bottom, lift the whole block and fix upwards
    const limit = cy + outerRadius + ELBOW + 8;
    const overflow = sideItems.length ? sideItems[sideItems.length - 1].ey - limit : 0;
    if (overflow > 0) for (const item of sideItems) item.ey -= overflow;
    for (let i = sideItems.length - 2; i >= 0; i--) {
      if (sideItems[i + 1].ey - sideItems[i].ey < GAP) sideItems[i].ey = sideItems[i + 1].ey - GAP;
    }
  }

  return new Map(items.map((i) => [i.id, i]));
};

// One list row: colored bullet + name + value (+ percentage on category rows).
// `indent` shifts the row right when it sits inside an expanded "Outros".
const BreakdownRow = ({ color, icon, name, value, percent, child, selected, expandable, open, onClick, onHover, indent = 0 }) => (
  <Box
    onClick={onClick}
    onMouseEnter={onHover ? () => onHover(true) : undefined}
    onMouseLeave={onHover ? () => onHover(false) : undefined}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      py: child ? 0.75 : 1.25,
      pl: (child ? 3 : 1) + indent,
      pr: 1,
      cursor: onClick ? 'pointer' : 'default',
      borderBottom: '1px solid',
      borderColor: 'divider',
      bgcolor: selected ? 'action.hover' : 'transparent',
      transition: 'background-color 120ms',
      '&:hover': onClick ? { bgcolor: 'action.hover' } : undefined,
    }}
  >
    <Box
      sx={{
        width: child ? 24 : 34,
        height: child ? 24 : 34,
        borderRadius: '50%',
        bgcolor: color,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '& svg': { fontSize: child ? 14 : 19 },
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{ flex: 1, fontSize: child ? 13 : 15, fontWeight: child ? 400 : 500 }}
      noWrap
      title={name}
    >
      {name}
    </Typography>
    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
      <Typography sx={{ fontSize: child ? 13 : 15, fontWeight: child ? 500 : 700, lineHeight: 1.3 }}>
        {formatNumber(value)}
      </Typography>
      {percent != null && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
          {formatPercent(percent)}
        </Typography>
      )}
    </Box>
    {/* Arrow signalling that the row opens the next level */}
    <ExpandMoreIcon
      sx={{
        fontSize: child ? 16 : 20,
        flexShrink: 0,
        color: 'text.secondary',
        visibility: expandable ? 'visible' : 'hidden',
        transform: open ? 'rotate(180deg)' : 'none',
        transition: 'transform 160ms',
      }}
    />
  </Box>
);

// Third level: the entries that add up to a cost center / client, paginated.
// onEntryClick makes each entry actionable (the expenses block opens the entry for
// editing); without it the list stays the read-only detail it has always been.
const EntryList = ({ entries, color, indent = 0, onEntryClick }) => {
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [entries]);

  const pageCount = Math.max(1, Math.ceil(entries.length / ENTRY_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const offset = (currentPage - 1) * ENTRY_PAGE_SIZE;
  const visible = entries.slice(offset, offset + ENTRY_PAGE_SIZE);
  // `badge` is a column of its own (the installment on received payments). Reserved
  // for the whole list so the values below it stay aligned, page after page.
  const hasBadge = entries.some((entry) => entry.badge);

  return (
    <Box
      sx={{
        pl: 6 + indent,
        pr: 1,
        bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.035)'),
      }}
    >
      {hasBadge && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, px: 1, pt: 0.6 }}>
          <Box sx={{ flex: 1, minWidth: 0 }} />
          <Typography sx={{ fontSize: 10, color: 'text.secondary', minWidth: 40, textAlign: 'right' }}>
            Parcela
          </Typography>
          <Typography sx={{ fontSize: 10, color: 'text.secondary', minWidth: 82, textAlign: 'right' }}>
            Valor
          </Typography>
        </Box>
      )}

      {visible.map((entry) => (
        <Box
          key={entry.id}
          onClick={onEntryClick ? () => onEntryClick(entry) : undefined}
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
            px: 1,
            py: 0.4,
            borderBottom: '1px solid',
            borderColor: 'divider',
            cursor: onEntryClick ? 'pointer' : 'default',
            '&:hover': onEntryClick ? { bgcolor: 'action.selected' } : undefined,
          }}
        >
          <Typography
            sx={{ flex: 1, minWidth: 0, fontSize: 11.5 }}
            noWrap
            title={onEntryClick
              ? `${entry.primary} ${entry.secondary || ''} — clique para editar`
              : `${entry.primary} ${entry.secondary || ''}`}
          >
            {entry.primary}
            {entry.secondary && (
              <Box component="span" sx={{ color: 'text.secondary', ml: 0.75 }}>· {entry.secondary}</Box>
            )}
          </Typography>
          {hasBadge && (
            <Typography
              sx={{ fontSize: 11.5, color: 'text.secondary', minWidth: 40, textAlign: 'right', whiteSpace: 'nowrap' }}
              title={entry.badgeTitle}
            >
              {entry.badge || '—'}
            </Typography>
          )}
          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color, whiteSpace: 'nowrap', minWidth: 82, textAlign: 'right' }}>
            {formatNumber(entry.value)}
          </Typography>
        </Box>
      ))}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 1, py: 0.5 }}>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          {entries.length} {entries.length === 1 ? 'lançamento' : 'lançamentos'}
        </Typography>
        {pageCount > 1 && (
          <Pagination
            size="small"
            count={pageCount}
            page={currentPage}
            onChange={(_, p) => setPage(p)}
            siblingCount={0}
            boundaryCount={1}
          />
        )}
      </Box>
    </Box>
  );
};

// Second level of a category: search + pagination (keeps the height stable)
const CategoryChildren = ({ items, color, icon, indent = 0, keepZeroValues = false, childSort = 'value', onEntryClick }) => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [openChild, setOpenChild] = useState(null);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    const base = items.filter((i) => (keepZeroValues ? i.value >= 0 : i.value > 0));
    // By weight, the default reading of a breakdown. 'source' keeps the order the API
    // sent instead: where the ordering is part of the report, the backend owns it.
    if (childSort !== 'source') base.sort((a, b) => b.value - a.value);
    return term ? base.filter((i) => (i.name || '').toLowerCase().includes(term)) : base;
  }, [items, query, keepZeroValues, childSort]);

  useEffect(() => { setPage(1); setOpenChild(null); }, [query]);

  if (!items.length) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', pl: 3 + indent, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        Sem detalhamento disponível
      </Typography>
    );
  }

  const pageCount = Math.max(1, Math.ceil(filtered.length / CHILD_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const offset = (currentPage - 1) * CHILD_PAGE_SIZE;
  const visible = filtered.slice(offset, offset + CHILD_PAGE_SIZE);
  const filteredTotal = filtered.reduce((acc, i) => acc + i.value, 0);

  return (
    <Box sx={{ bgcolor: 'action.hover' }}>
      {items.length > CHILD_PAGE_SIZE && (
        <Box sx={{ pl: 3 + indent, pr: 3, py: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Buscar item..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {visible.map((child, i) => {
        const key = `${child.name}-${i}`;
        const entries = child.entries || [];
        const isOpen = openChild === key;
        return (
          <Box key={key}>
            <BreakdownRow
              color={color}
              icon={icon}
              name={child.name}
              value={child.value}
              child
              expandable={entries.length > 0}
              open={isOpen}
              selected={isOpen}
              onClick={entries.length ? () => setOpenChild(isOpen ? null : key) : undefined}
              indent={indent}
            />
            <Collapse in={isOpen} unmountOnExit>
              <EntryList entries={entries} color={color} indent={indent} onEntryClick={onEntryClick} />
            </Collapse>
          </Box>
        );
      })}

      {!visible.length && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 3 + indent, py: 1 }}>
          Nenhum item encontrado para "{query}"
        </Typography>
      )}

      {!!filtered.length && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, pl: 3 + indent, pr: 1, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {filtered.length} {filtered.length === 1 ? 'item' : 'itens'} · {formatNumber(filteredTotal)}
          </Typography>
          {pageCount > 1 && (
            <Pagination
              size="small"
              count={pageCount}
              page={currentPage}
              onChange={(_, p) => { setPage(p); setOpenChild(null); }}
              siblingCount={0}
              boundaryCount={1}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

// Is this category worth opening? Either it breaks down further, or it is the
// "Outros" aggregate, which opens into the categories it folded.
const isExpandable = (slice, keepZeroValues = false) =>
  !!slice.nested?.length
  || (slice.children || []).some((c) => (keepZeroValues ? c.value >= 0 : c.value > 0));

// The rows of a category list. Rendered at the top level of the block and again
// inside an expanded "Outros" — where each folded category behaves exactly as it
// would have on the outside, breakdown and all.
const CategoryRows = ({
  slices, total, expanded, setExpanded, highlighted, setHovered,
  nestedExpanded, setNestedExpanded, indent = 0, keepZeroValues = false, childSort = 'value',
  onEntryClick,
}) =>
  slices.map((slice) => {
    const percent = total ? (slice.value / total) * 100 : 0;
    const expandable = isExpandable(slice, keepZeroValues);
    const open = expanded === slice.id;
    return (
      <Box key={slice.id}>
        <BreakdownRow
          color={slice.color}
          icon={slice.icon}
          name={slice.name}
          value={slice.value}
          percent={percent}
          selected={highlighted === slice.id}
          expandable={expandable}
          open={open}
          onClick={expandable ? () => setExpanded(open ? null : slice.id) : undefined}
          onHover={setHovered ? (on) => setHovered(on ? slice.id : null) : undefined}
          indent={indent}
        />
        <Collapse in={open} unmountOnExit>
          {slice.nested?.length ? (
            // The folded categories: same rows, same donut sync — while this is
            // open they have their own slices in the ring
            <Box sx={{ bgcolor: 'action.hover' }}>
              <CategoryRows
                slices={slice.nested}
                total={total}
                expanded={nestedExpanded}
                setExpanded={setNestedExpanded}
                highlighted={highlighted}
                setHovered={setHovered}
                indent={indent + 2}
                keepZeroValues={keepZeroValues}
                childSort={childSort}
                onEntryClick={onEntryClick}
              />
            </Box>
          ) : (
            <CategoryChildren
              items={slice.children || []}
              color={slice.color}
              icon={slice.icon}
              indent={indent}
              keepZeroValues={keepZeroValues}
              childSort={childSort}
              onEntryClick={onEntryClick}
            />
          )}
        </Collapse>
      </Box>
    );
  });

// A full block: grouped list on the left + donut on the right.
// Shared by every financial report that breaks a total down into categories.
export default function BreakdownBlock({
  title, categories, mode, emptyLabel, totalLabel = 'Total', keepZeroValues = false,
  maxSlices = MAX_SLICES, foldBelow = 0, childSort = 'value', onEntryClick,
}) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(null);
  const [nestedExpanded, setNestedExpanded] = useState(null);
  const [hovered, setHovered] = useState(null);

  const { slices, total } = useMemo(
    () => buildSlices(categories, mode, keepZeroValues, maxSlices, foldBelow),
    [categories, mode, keepZeroValues, maxSlices, foldBelow]
  );

  // Opening "Outros" splits its slice: the categories it folded take its place
  // in the ring, each with the color it already has on the list
  const drawn = useMemo(() => {
    const other = slices.find((s) => s.nested?.length);
    if (!other || expanded !== other.id) return slices;
    return [...slices.filter((s) => s !== other), ...other.nested];
  }, [slices, expanded]);

  // Closing "Outros" must not leave a folded category open behind it
  useEffect(() => { setNestedExpanded(null); }, [expanded]);

  // Recharts spreads the data object into <Sector>; send only what it needs
  const pieData = useMemo(
    () => drawn.map(({ id, name, value }) => ({ id, name, value })),
    [drawn]
  );

  const surface = theme.palette.background.paper;
  const labelColor = theme.palette.text.secondary;

  // Outer labels (leader line + %) with collision avoidance; layout depends only on geometry
  const renderLabel = useMemo(() => {
    const cache = { key: null, layout: null };
    return ({ cx, cy, outerRadius, index }) => {
      const slice = drawn[index];
      if (!slice) return null;
      const key = `${cx}|${cy}|${outerRadius}`;
      if (cache.key !== key) {
        cache.key = key;
        cache.layout = buildLabelLayout(
          drawn, total, { cx, cy, outerRadius },
          drawn.length > slices.length || drawn.length > MAX_SLICES ? LABEL_MIN_PERCENT : 0,
        );
      }
      const pos = cache.layout.get(slice.id);
      if (!pos) return null;
      const tx = cx + pos.side * (outerRadius + 30);
      return (
        <g key={slice.id} style={{ pointerEvents: 'none' }}>
          <path
            d={`M${pos.sx},${pos.sy}L${pos.ex},${pos.ey}L${tx},${pos.ey}`}
            stroke={pos.color}
            strokeWidth={1.5}
            fill="none"
          />
          <text
            x={tx + pos.side * 5}
            y={pos.ey}
            textAnchor={pos.side === 1 ? 'start' : 'end'}
            dominantBaseline="central"
            fontSize={12}
            fill={labelColor}
          >
            {formatPercent(pos.percent)}
          </text>
        </g>
      );
    };
  }, [drawn, slices, total, labelColor]);

  if (!slices.length) {
    return (
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>
      </Box>
    );
  }

  // While "Outros" is split there is no slice for it to highlight, so the ring
  // simply shows the split instead of dimming whole
  const exploded = drawn.length > slices.length;
  const highlighted = hovered ?? nestedExpanded ?? (exploded ? null : expanded);
  const highlightedSlice = drawn.find((s) => s.id === highlighted);
  // Only categories that actually have a breakdown expand (also on a slice click).
  // A slice born from the split "Outros" opens inside it, not at the top level.
  const toggle = (id) => {
    const slice = drawn.find((s) => s.id === id);
    if (!slice || !isExpandable(slice, keepZeroValues)) return;
    const setter = slices.includes(slice) ? setExpanded : setNestedExpanded;
    setter((current) => (current === id ? null : id));
  };

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>{title}</Typography>
      {/* Top aligned on purpose: expanding the list must not move the donut */}
      <Grid container spacing={2} alignItems="flex-start">
        {/* Grouped list */}
        <Grid item xs={12} md={7}>
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            <CategoryRows
              slices={slices}
              total={total}
              expanded={expanded}
              setExpanded={setExpanded}
              highlighted={highlighted}
              setHovered={setHovered}
              nestedExpanded={nestedExpanded}
              setNestedExpanded={setNestedExpanded}
              keepZeroValues={keepZeroValues}
              childSort={childSort}
              onEntryClick={onEntryClick}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 1.5, pt: 1.5, pr: 1 }}>
              <Typography variant="body2" color="text.secondary">{totalLabel}</Typography>
              <Typography sx={{ fontSize: 17, fontWeight: 700 }}>{formatNumber(total)}</Typography>
            </Box>
          </Box>
        </Grid>

        {/* Donut */}
        <Grid item xs={12} md={5}>
          <Box sx={{ position: 'relative', height: 340 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 16, right: 56, bottom: 16, left: 56 }}>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="58%"
                  outerRadius="88%"
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={0}
                  stroke={surface}
                  strokeWidth={2}
                  isAnimationActive={false}
                  labelLine={false}
                  label={renderLabel}
                  onClick={(entry) => entry?.id && toggle(entry.id)}
                  onMouseEnter={(entry) => setHovered(entry?.id ?? null)}
                  onMouseLeave={() => setHovered(null)}
                >
                  {drawn.map((slice) => (
                    <Cell
                      key={slice.id}
                      fill={slice.color}
                      fillOpacity={!highlighted || highlighted === slice.id ? 1 : 0.25}
                      style={{ cursor: 'pointer', outline: 'none' }}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            {/* Donut center: the block total, or the highlighted category */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                px: 10,
              }}
            >
              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '100%' }}>
                {highlightedSlice ? highlightedSlice.name : totalLabel}
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                {formatNumber(highlightedSlice ? highlightedSlice.value : total)}
              </Typography>
              {highlightedSlice && total > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {formatPercent((highlightedSlice.value / total) * 100)}
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
